import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { fmtCurrencyFull, fmtPct } from "@/lib/format";
import { Target, Loader2 } from "lucide-react";
import { errorMessage } from "@/lib/errorMessage";
import { toast } from "sonner";

export default function GoalSeekDialog({ open, onOpenChange, inputs, onApply }) {
  const [target, setTarget] = useState(85);
  const [solveFor, setSolveFor] = useState("monthly_contribution");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/projections/solve", {
        ...inputs,
        target_success_pct: target,
        solve_for: solveFor,
      });
      setResult(data);
      if (!data.solved) {
        toast.error(data.reason || "Goal not reachable with these inputs.");
      } else {
        toast.success("Goal-seek complete.");
      }
    } catch (e) {
      console.error(e);
      toast.error(errorMessage(e, "Solve failed."));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!result?.solved) return;
    const patch = { ...inputs };
    if (solveFor === "monthly_contribution") patch.monthly_contribution = Math.ceil(result.value);
    if (solveFor === "retirement_age") patch.retirement_age = result.value;
    if (solveFor === "current_savings") patch.current_savings = Math.round(result.value);
    if (solveFor === "annual_withdrawal") patch.annual_withdrawal = Math.round(result.value);
    onApply(patch);
    onOpenChange(false);
  };

  const formatValue = () => {
    if (!result?.solved) return "";
    if (solveFor === "monthly_contribution") return `${fmtCurrencyFull(result.value)}/mo`;
    if (solveFor === "retirement_age") return `Age ${result.value}`;
    if (solveFor === "current_savings") return `${fmtCurrencyFull(result.value)} starting`;
    if (solveFor === "annual_withdrawal") return `${fmtCurrencyFull(result.value)}/yr withdrawal`;
    return "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="goalseek-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#1C2B23] flex items-center gap-2">
            <Target size={20} className="text-[#2B5339]" /> Goal seek
          </DialogTitle>
          <DialogDescription className="text-[#5C6B62]">
            Solve for the input that achieves your target success probability.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Solve for
            </Label>
            <Select value={solveFor} onValueChange={setSolveFor}>
              <SelectTrigger data-testid="goal-solve-for" className="mt-2 h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly_contribution">Monthly contribution</SelectItem>
                <SelectItem value="retirement_age">Retirement age</SelectItem>
                <SelectItem value="current_savings">Current savings (starting balance)</SelectItem>
                <SelectItem value="annual_withdrawal">Max safe annual withdrawal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
              Target success probability
            </Label>
            <div className="mt-2 flex items-center bg-white border border-[#E5E2DC] rounded-lg focus-within:border-[#2B5339]">
              <Input
                data-testid="goal-target-pct"
                type="number"
                step={1}
                min={50}
                max={99}
                value={target}
                onChange={(e) => setTarget(Number(e.target.value || 0))}
                className="border-0 bg-transparent focus-visible:ring-0 font-mono-num text-[#1C2B23]"
              />
              <span className="pr-3 text-[#5C6B62] text-sm">%</span>
            </div>
          </div>

          <button
            data-testid="goal-run-btn"
            type="button"
            disabled={loading}
            onClick={run}
            className="w-full bg-[#2B5339] text-white hover:bg-[#1E3B28] disabled:opacity-60 rounded-full py-3 text-sm inline-flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Target size={14} />}
            {loading ? "Searching…" : "Run goal seek"}
          </button>

          {result && (
            <div data-testid="goal-result" className="mt-4 rounded-xl border border-[#E5E2DC] bg-[#F9F8F6] p-5">
              {result.solved ? (
                <>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C6B62] font-semibold">
                    Solution found
                  </div>
                  <div className="font-display text-3xl text-[#1C2B23] font-mono-num mt-1">
                    {formatValue()}
                  </div>
                  <div className="text-sm text-[#5C6B62] mt-1">
                    Hits {fmtPct(result.summary.success_probability_pct, 1)} success ·
                    median final {fmtCurrencyFull(result.summary.median_final_balance)}
                  </div>
                  <button
                    data-testid="goal-apply"
                    type="button"
                    onClick={apply}
                    className="mt-4 bg-transparent border border-[#2B5339] text-[#2B5339] hover:bg-[#F0EFEA] rounded-full px-5 py-2.5 text-sm w-full"
                  >
                    Apply &amp; re-run projection
                  </button>
                </>
              ) : (
                <div className="text-sm text-[#C26747]">
                  Couldn't find a solution: {result.reason || "infeasible"}
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
